import React, { useCallback } from 'react';
import { Button, Modal, Spin } from 'antd';
import { useAppDispatch, useAppSelector, useBlocker } from 'common/hooks';
import { RouterPrompt } from 'common/components';
import { getFormattedTime } from 'common/helpers';
import { ITransition } from 'common/models';
import { EEventActionType, EEventStatus } from 'entities/Events/Event.enum';
import { EModalKey } from 'entities/Modal/Modal.enum';

interface IComponentProps {
  eventId: number;
  blockRouter: boolean;
}

export const EventRouterHandler: React.FC<IComponentProps> = ({ eventId, blockRouter }) => {
  const { event, eventLoading, eventAction, eventText, eventPhysicianId, timerLoading, timerIsRunning, timerUntrackedTime } =
    useAppSelector((state) => ({
      event: state.event.data,
      eventLoading: state.event.loading,
      eventAction: state.event.action,
      eventText: state.event.text,
      eventPhysicianId: state.event.physicianId,
      timerLoading: state.timer.loading,
      timerIsRunning: state.timer.isRunning,
      timerUntrackedTime: state.timer.untrackedTime,
    }));
  const { event: eventDispatch, timer: timerDispatch, modal: modalDispatch } = useAppDispatch();

  const eventStatus = event?.status;

  const blocker = useCallback(
    (autoUnblockingTx: ITransition) => {
      switch (true) {
        case eventAction === EEventActionType.Complete: {
          eventDispatch.completeEvent({
            id: eventId,
            text: eventText as string,
            onSuccess: () => {
              modalDispatch.hideModal(EModalKey.Complete);
              autoUnblockingTx.retry();
            },
          });
          break;
        }
        case blockRouter && autoUnblockingTx.action === 'PUSH':
        case blockRouter && autoUnblockingTx.action === 'POP' && !eventAction:
        case blockRouter && eventAction === EEventActionType.MoveToInbox:
        case blockRouter && eventAction === EEventActionType.Back: {
          eventDispatch.moveEventToInbox({
            id: eventId,
            onSuccess: () => autoUnblockingTx.retry(),
          });
          break;
        }
        default: {
          autoUnblockingTx.retry();
          break;
        }
      }
    },
    [eventAction, eventText, eventPhysicianId, blockRouter],
  );

  useBlocker(blockRouter && !timerIsRunning, blocker);

  const renderPrompt = (isActive: boolean, onConfirm?: (value: unknown) => void, onCancel?: (value: unknown) => void) => {
    return (
      isActive && (
        <Modal title="New Notification" open={true} footer={false} onCancel={onCancel} width={600}>
          <Spin spinning={timerLoading || eventLoading}>
            <div className="modal__body">
              <p>
                From the review start you’ve spent {getFormattedTime(timerUntrackedTime, 'HH:mm:ss')}, but haven’t stopped the
                timer. Save time or delete?
              </p>
            </div>

            <div className="d-flex align-center flex-wrap" style={{ gap: 24 }}>
              <Button
                className="blue"
                onClick={() => {
                  eventStatus === EEventStatus.UnderReview
                    ? timerDispatch.stopTimer({ eventId, onSuccess: () => onConfirm?.(undefined) })
                    : onConfirm?.(undefined);
                }}
              >
                Save and Leave
              </Button>

              <Button
                className="orange"
                onClick={() => {
                  eventStatus === EEventStatus.UnderReview
                    ? timerDispatch.deleteTimer({ eventId, onSuccess: () => onConfirm?.(undefined) })
                    : onConfirm?.(undefined);
                }}
              >
                Delete and Leave
              </Button>

              <Button className="default" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </Spin>
        </Modal>
      )
    );
  };

  return <RouterPrompt when={timerIsRunning} renderPrompt={renderPrompt} />;
};
